// /src/pages/private/members/onboarding/MembersOnboardingHeroBackground.jsx
// Shared full-bleed image / video (incl. HLS) for member onboarding welcome + ending screens.

import React, { useEffect, useMemo, useRef } from 'react';
import Hls from 'hls.js';
import { onboardingBackgroundKind } from '../../../../utils/onboardingLandingUi';

export function clamp01(n) {
  if (!Number.isFinite(n)) return 0.5;
  return Math.min(1, Math.max(0, n));
}

const DEFAULT_IMAGE_CLASS =
  'members-onboarding-welcome__media-el members-onboarding-welcome__media-el--image';
const DEFAULT_VIDEO_CLASS =
  'members-onboarding-welcome__media-el members-onboarding-welcome__media-el--video';

export default function MembersOnboardingHeroBackground({
  media,
  imageClassName = DEFAULT_IMAGE_CLASS,
  videoClassName = DEFAULT_VIDEO_CLASS,
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const kind = useMemo(() => onboardingBackgroundKind(media), [media]);

  useEffect(() => {
    const video = videoRef.current;
    const srcUrl = media?.url ? String(media.url).trim() : '';
    if (!video || !srcUrl) return undefined;

    if (kind === 'hls') {
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = srcUrl;
        const onCanPlay = () => {
          video.play().catch(() => {});
        };
        video.addEventListener('canplay', onCanPlay, { once: true });
        return () => {
          video.removeEventListener('canplay', onCanPlay);
          video.removeAttribute('src');
          video.load();
        };
      }

      if (!Hls.isSupported()) {
        return undefined;
      }

      const hls = new Hls({
        startLevel: 0,
        autoStartLoad: true,
        capLevelToPlayerSize: false,
      });

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(srcUrl);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data?.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          hls.destroy();
          hlsRef.current = null;
        }
      });

      hls.attachMedia(video);
      hlsRef.current = hls;

      return () => {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        video.removeAttribute('src');
        video.load();
      };
    }

    if (kind === 'video') {
      video.src = srcUrl;
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    return undefined;
  }, [kind, media?.url]);

  if (kind === 'none') return null;

  if (kind === 'image') {
    return (
      <img
        className={imageClassName}
        src={media.url}
        alt=""
        loading="eager"
        decoding="async"
      />
    );
  }

  return (
    <video
      ref={videoRef}
      className={videoClassName}
      playsInline
      muted
      loop
      autoPlay
      controls={false}
      aria-hidden
    />
  );
}
